<template>
  <div class="loginimg" style="display: flex; flex-direction: column;">
    <div style="margin: auto 0; width: 100%;">

    <CustomerLogo />

    <!-- Email Confirmation Success Screen -->
    <div v-if="registrationComplete" class="row justify-content-center">
      <div class="col-10 col-md-6 col-lg-5 col-xl-4 align-self-center justify-content-center row">
        <div class="col-12 text-center mb-6">
          <v-icon size="80" color="white">mdi-email-check-outline</v-icon>
        </div>
        <div class="white--text col-12 text-h4 text-center mb-4">
          {{ $t('registration_email_sent_title') }}
        </div>
        <div class="white--text col-10 text-center mb-6" style="opacity: 0.85; font-size: 16px; line-height: 1.5;">
          {{ $t('registration_email_sent_message') }}
        </div>
        <div class="white--text col-10 text-center mb-8" style="opacity: 0.7; font-size: 14px;">
          <v-icon small color="white" class="mr-1">mdi-information-outline</v-icon>
          {{ $t('registration_check_spam') }}
        </div>
        <div class="pa-0 col-10 mb-4">
          <v-btn
              class="w-100 white--text"
              :class="resendCooldown === 0 && !isResending ? 'signinbtn' : ''"
              :style="resendCooldown === 0 && !isResending ? 'padding: 27px !important; background-color: #161616 !important;' : 'padding: 27px !important; background-color: #666666 !important; border: 1px solid #666666 !important;'"
              outlined
              tile
              :disabled="resendCooldown > 0 || isResending"
              :loading="isResending"
              @click="resendEmail">
            <template v-if="isResending">
              {{ $t('sending_email') }}
            </template>
            <template v-else-if="resendCooldown > 0">
              {{ $t('resend_available_in') }} ({{ resendCooldown }}s)
            </template>
            <template v-else>
              {{ $t('resend_email') }}
            </template>
          </v-btn>
        </div>
        <div class="pa-0 col-10 mb-4">
          <v-btn
              class="w-100 white--text signinbtn"
              style="padding: 27px !important; background-color: #161616 !important;"
              outlined
              tile
              @click="$router.push('/login')">
            {{ $t('back_to_login') }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Registration Form -->
    <div v-else class="row justify-content-center">
      <div class="col-10 col-md-6 col-lg-5 col-xl-4 align-self-center justify-content-center row">
        <div class="white--text col-12 text-h4 text-center">
          {{ $t('sign_up_for_muniman') }}
        </div>

        <!-- Customer Type Tabs -->
        <div class="col-10 mb-8 pa-0 mt-6">
          <div class="white--text mb-2 text-uppercase" style="font-size: 12px; letter-spacing: 0.86px; opacity: 0.85;">
            {{ $t('customer_type') }}
          </div>
          <div class="d-flex" style="gap: 12px;">
            <v-btn
                class="rounded-0"
                :class="clientType === 'Organisation' ? 'white black--text' : 'black white--text'"
                tile
                elevation="0"
                @click="clientType = 'Organisation'"
                style="border: 1px solid white !important; height: 88px; text-transform: none; font-size: 13px; padding: 16px 8px !important; flex: 1 1 0; min-width: 0;"
            >
              <div class="d-flex flex-column align-center">
                <v-icon size="28" :color="clientType === 'Organisation' ? 'black' : 'white'" class="mb-2">
                  mdi-office-building
                </v-icon>
                <span style="line-height: 1.2;">{{ $t('organisation') }}</span>
              </div>
            </v-btn>
            <v-btn
                class="rounded-0"
                :class="clientType === 'Company' ? 'white black--text' : 'black white--text'"
                tile
                elevation="0"
                @click="clientType = 'Company'"
                style="border: 1px solid white !important; height: 88px; text-transform: none; font-size: 13px; padding: 16px 8px !important; flex: 1 1 0; min-width: 0;"
            >
              <div class="d-flex flex-column align-center">
                <v-icon size="28" :color="clientType === 'Company' ? 'black' : 'white'" class="mb-2">mdi-domain</v-icon>
                <span style="line-height: 1.2;">{{ $t('company') }}</span>
              </div>
            </v-btn>
            <v-btn
                class="rounded-0"
                :class="clientType === 'PrivatePerson' ? 'white black--text' : 'black white--text'"
                tile
                elevation="0"
                @click="clientType = 'PrivatePerson'"
                style="border: 1px solid white !important; height: 88px; text-transform: none; font-size: 13px; padding: 16px 8px !important; flex: 1 1 0; min-width: 0;"
            >
              <div class="d-flex flex-column align-center">
                <v-icon size="28" :color="clientType === 'PrivatePerson' ? 'black' : 'white'" class="mb-2">mdi-account
                </v-icon>
                <span style="line-height: 1.2;">{{ $t('private_person') }}</span>
              </div>
            </v-btn>
          </div>
        </div>
        <div class="col-10 pa-0 d-flex flex-column">
          <!-- Organisation Form -->
          <div v-if="clientType === 'Organisation'" class="pa-0">
            <v-text-field
                data-field="organisationName"
                class="col-12 mb-8 white rounded-0 logininput"
                :label="$t('organisation_name')"
                :error="errors.organisationName"
                outlined
                hide-details="auto"
                v-model="organisationData.name"
                @input="errors.organisationName = false">
            </v-text-field>

            <v-text-field
                data-field="streetAddress"
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('street_and_house_number')"
                :error="errors.streetAddress"
                outlined
                hide-details="auto"
                v-model="organisationData.streetAddress"
                @input="errors.streetAddress = false">
            </v-text-field>

            <div class="d-flex col-12 pa-0 mb-4">
              <v-text-field
                  data-field="zipCode"
                  class="white rounded-0 logininput mr-2"
                  :label="$t('postal_code')"
                  :error="errors.zipCode"
                  outlined
                  hide-details="auto"
                  v-model="organisationData.zipCode"
                  @input="errors.zipCode = false"
                  style="flex: 1;">
              </v-text-field>
              <v-text-field
                  data-field="city"
                  class="white rounded-0 logininput ml-2"
                  :label="$t('city')"
                  :error="errors.city"
                  outlined
                  hide-details="auto"
                  v-model="organisationData.city"
                  @input="errors.city = false"
                  style="flex: 1;">
              </v-text-field>
            </div>

            <v-autocomplete
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('country_label')"
                outlined
                hide-details="auto"
                v-model="organisationData.country"
                :items="countries">
            </v-autocomplete>

            <v-text-field
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('phone_number_optional')"
                placeholder="+49 123 4567890"
                outlined
                hide-details="auto"
                v-model="organisationData.phoneNumber">
            </v-text-field>
          </div>

          <!-- Company Form -->
          <div v-if="clientType === 'Company'" class="pa-0">
            <v-text-field
                data-field="companyName"
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('company_name')"
                :error="errors.companyName"
                outlined
                hide-details="auto"
                v-model="companyData.name"
                @input="errors.companyName = false">
            </v-text-field>

            <v-select
                data-field="businessForm"
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('business_form')"
                :error="errors.businessForm"
                outlined
                hide-details="auto"
                v-model="companyData.formOfBusiness"
                @change="errors.businessForm = false"
                :items="['GmbH', 'AG', 'UG', 'KG', 'OHG', 'Einzelunternehmen']">
            </v-select>

            <v-text-field
                data-field="vat"
                class="col-12 mb-8 white rounded-0 logininput"
                :label="$t('vat_id')"
                :error="errors.vat"
                outlined
                hide-details="auto"
                v-model="companyData.vat"
                @input="errors.vat = false">
            </v-text-field>

            <v-text-field
                data-field="streetAddress"
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('street_and_house_number')"
                :error="errors.streetAddress"
                outlined
                hide-details="auto"
                v-model="companyData.streetAddress"
                @input="errors.streetAddress = false">
            </v-text-field>

            <div class="d-flex col-12 pa-0 mb-4">
              <v-text-field
                  data-field="zipCode"
                  class="white rounded-0 logininput mr-2"
                  :label="$t('postal_code')"
                  :error="errors.zipCode"
                  outlined
                  hide-details="auto"
                  v-model="companyData.zipCode"
                  @input="errors.zipCode = false"
                  style="flex: 1;">
              </v-text-field>
              <v-text-field
                  data-field="city"
                  class="white rounded-0 logininput ml-2"
                  :label="$t('city')"
                  :error="errors.city"
                  outlined
                  hide-details="auto"
                  v-model="companyData.city"
                  @input="errors.city = false"
                  style="flex: 1;">
              </v-text-field>
            </div>

            <v-autocomplete
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('country_label')"
                outlined
                hide-details="auto"
                v-model="companyData.country"
                :items="countries">
            </v-autocomplete>

            <v-text-field
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('phone_number_optional')"
                outlined
                hide-details="auto"
                v-model="companyData.phoneNumber">
            </v-text-field>
          </div>

          <!-- Personal Information Section for Private Person (shown first) -->
          <div v-if="clientType === 'PrivatePerson'" class="pa-0">
            <div class="white--text mb-2 text-uppercase"
                 style="font-size: 12px; letter-spacing: 0.86px; opacity: 0.85;">
              {{ $t('personal_information') }}
            </div>

            <div class="d-flex col-12 pa-0 mb-4">
              <v-text-field
                  data-field="firstName"
                  class="white rounded-0 logininput mr-2"
                  :label="$t('firstname')"
                  :error="errors.firstName"
                  outlined
                  hide-details="auto"
                  v-model="personalData.user.firstName"
                  @input="errors.firstName = false"
                  style="flex: 1;">
              </v-text-field>
              <v-text-field
                  data-field="lastName"
                  class="white rounded-0 logininput ml-2"
                  :label="$t('lastname')"
                  :error="errors.lastName"
                  outlined
                  hide-details="auto"
                  v-model="personalData.user.lastName"
                  @input="errors.lastName = false"
                  style="flex: 1;">
              </v-text-field>
            </div>

            <v-text-field
                data-field="email"
                class="col-12 mb-8 white rounded-0 logininput"
                :label="$t('email')"
                :error="errors.email"
                outlined
                hide-details="auto"
                v-model="personalData.user.email"
                @input="errors.email = false">
            </v-text-field>

            <div class="white--text mb-2 text-uppercase"
                 style="font-size: 12px; letter-spacing: 0.86px; opacity: 0.85;">
              {{ $t('address') }}
            </div>

            <v-text-field
                data-field="streetAddress"
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('street_and_house_number')"
                :error="errors.streetAddress"
                outlined
                hide-details="auto"
                v-model="personalData.streetAddress"
                @input="errors.streetAddress = false">
            </v-text-field>

            <div class="d-flex col-12 pa-0 mb-4">
              <v-text-field
                  data-field="zipCode"
                  class="white rounded-0 logininput mr-2"
                  :label="$t('postal_code')"
                  :error="errors.zipCode"
                  outlined
                  hide-details="auto"
                  v-model="personalData.zipCode"
                  @input="errors.zipCode = false"
                  style="flex: 1;">
              </v-text-field>
              <v-text-field
                  data-field="city"
                  class="white rounded-0 logininput ml-2"
                  :label="$t('city')"
                  :error="errors.city"
                  outlined
                  hide-details="auto"
                  v-model="personalData.city"
                  @input="errors.city = false"
                  style="flex: 1;">
              </v-text-field>
            </div>

            <v-autocomplete
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('country_label')"
                outlined
                hide-details="auto"
                v-model="personalData.country"
                :items="countries">
            </v-autocomplete>

            <v-text-field
                class="col-12 mb-4 white rounded-0 logininput"
                :label="$t('phone_number_optional')"
                outlined
                hide-details="auto"
                v-model="personalData.phoneNumber">
            </v-text-field>
          </div>

          <!-- Personal Information Section for Organisation and Company -->
          <div v-if="clientType !== 'PrivatePerson'" class="pa-0 mt-4">
            <div class="white--text mb-2 text-uppercase"
                 style="font-size: 12px; letter-spacing: 0.86px; opacity: 0.85;">
              {{ $t('personal_information') }}
            </div>

            <div class="d-flex col-12 pa-0 mb-4">
              <v-text-field
                  data-field="firstName"
                  class="white rounded-0 logininput mr-2"
                  :label="$t('firstname')"
                  :error="errors.firstName"
                  outlined
                  hide-details="auto"
                  v-model="getCurrentUserData().user.firstName"
                  @input="errors.firstName = false"
                  style="flex: 1;">
              </v-text-field>
              <v-text-field
                  data-field="lastName"
                  class="white rounded-0 logininput ml-2"
                  :label="$t('lastname')"
                  :error="errors.lastName"
                  outlined
                  hide-details="auto"
                  v-model="getCurrentUserData().user.lastName"
                  @input="errors.lastName = false"
                  style="flex: 1;">
              </v-text-field>
            </div>

            <v-text-field
                data-field="email"
                class="col-12 mb-8 white rounded-0 logininput"
                :label="$t('email')"
                :error="errors.email"
                outlined
                hide-details="auto"
                v-model="getCurrentUserData().user.email"
                @input="errors.email = false">
            </v-text-field>
          </div>
        </div>

        <!-- Data Privacy Checkbox -->
        <div data-field="privacyPolicy" class="pa-0 col-10 mb-6">
          <v-checkbox
              v-model="privacyPolicyAccepted"
              :error="errors.privacyPolicy"
              hide-details="auto"
              @change="errors.privacyPolicy = false"
              dark
              color="white"
              class="mt-0">
            <template v-slot:label>
              <span class="white--text" style="font-size: 14px; opacity: 0.9;">
                {{ $t('accept_privacy_policy') }}
                <PrivacyPolicy :link-text="$t('privacy_policy_title')" />
                {{ $t('accept_privacy_policy_terms_separator') }}
                <TermsAndConditions :link-text="$t('terms_and_conditions_title')" />
              </span>
            </template>
          </v-checkbox>
        </div>

        <!-- Registration Button -->
        <div class="pa-0 col-10 mb-4">
          <v-btn
              class="w-100 white--text"
              :class="isFormValid && !isLoading ? 'signinbtn' : ''"
              :style="isFormValid && !isLoading ? 'padding: 27px !important; background-color: #161616 !important;' : 'padding: 27px !important; background-color: #666666 !important; border: 1px solid #666666 !important; cursor: pointer !important;'"
              outlined
              tile
              :disabled="isLoading"
              :loading="isLoading"
              @click="completeRegistration">
            {{ $t('complete_registration') }}
          </v-btn>
        </div>

        <!-- Loading overlay -->
        <v-overlay :value="isLoading" absolute opacity="0.3">
          <v-progress-circular indeterminate size="64" color="white"></v-progress-circular>
        </v-overlay>

        <!-- Already have account link -->
        <div class="pa-0 col-10 mb-8 text-center">
          <span class="white--text" style="opacity: 0.7; font-size: 14px;">
            {{ $t('already_have_account') }}
          </span>
          <v-btn
              text
              small
              class="white--text ml-1"
              style="text-transform: none; font-size: 14px; text-decoration: underline;"
              @click="$router.push('/login')">
            {{ $t('log_in') }}
          </v-btn>
        </div>
      </div>
    </div>

    </div>
  </div>
</template>
<script>
import PrivacyPolicy from './custom/PrivacyPolicy.vue';
import TermsAndConditions from './custom/TermsAndConditions.vue';
import CustomerLogo from "@/components/custom/CustomerLogo.vue";

export default {
  name: 'SignUp',
  components: {
    CustomerLogo,
    PrivacyPolicy,
    TermsAndConditions
  },

  data() {
    return {
      clientType: "Organisation", // Organisation is default, other options are Company and PrivatePerson
      organisationData: {
        name: "",
        streetAddress: "",
        city: "",
        zipCode: "",
        country: "Deutschland",
        phoneNumber: "",
        user: {
          firstName: "",
          lastName: "",
          email: "",
        }
      },
      companyData: {
        name: "",
        formOfBusiness: "",
        vat: "",
        streetAddress: "",
        city: "",
        zipCode: "",
        country: "Deutschland",
        phoneNumber: "",
        user: {
          firstName: "",
          lastName: "",
          email: "",
        }
      },
      personalData: {
        name: "",
        streetAddress: "",
        city: "",
        zipCode: "",
        country: "Deutschland",
        phoneNumber: "",
        user: {
          firstName: "",
          lastName: "",
          email: "",
        }
      },
      errors: {},
      registrationComplete: false,
      isLoading: false,
      isResending: false,
      resendCooldown: 30,
      resendTimer: null,
      lastRegistrationEmail: null,
      privacyPolicyAccepted: false
    }
  },
  beforeDestroy() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  },
  watch: {
    clientType: function (newVal, oldVal) {
        if(newVal != oldVal){
          this.errors = {};
        }    
    }
  },
  computed: {
    countries() {
      return [
        this.$t('country.germany'), this.$t('country.austria'), this.$t('country.switzerland'), this.$t('country.france'),
        this.$t('country.united_kingdom'), this.$t('country.united_states'), this.$t('country.afghanistan'), this.$t('country.albania'),
        this.$t('country.algeria'), this.$t('country.andorra'), this.$t('country.angola'), this.$t('country.antigua_and_barbuda'),
        this.$t('country.argentina'), this.$t('country.armenia'), this.$t('country.australia'), this.$t('country.azerbaijan'),
        this.$t('country.bahamas'), this.$t('country.bahrain'), this.$t('country.bangladesh'), this.$t('country.barbados'),
        this.$t('country.belarus'), this.$t('country.belgium'), this.$t('country.belize'), this.$t('country.benin'),
        this.$t('country.bhutan'), this.$t('country.bolivia'), this.$t('country.bosnia_and_herzegovina'), this.$t('country.botswana'),
        this.$t('country.brazil'), this.$t('country.brunei'), this.$t('country.bulgaria'), this.$t('country.burkina_faso'),
        this.$t('country.burundi'), this.$t('country.cabo_verde'), this.$t('country.cambodia'), this.$t('country.cameroon'),
        this.$t('country.canada'), this.$t('country.central_african_republic'), this.$t('country.chad'), this.$t('country.chile'),
        this.$t('country.china'), this.$t('country.colombia'), this.$t('country.comoros'), this.$t('country.congo_congo_brazzaville'),
        this.$t('country.costa_rica'), this.$t('country.croatia'), this.$t('country.cuba'), this.$t('country.cyprus'),
        this.$t('country.czechia_czech_republic'), this.$t('country.democratic_republic_of_the_congo'), this.$t('country.denmark'),
        this.$t('country.djibouti'), this.$t('country.dominica'), this.$t('country.dominican_republic'), this.$t('country.ecuador'),
        this.$t('country.egypt'), this.$t('country.el_salvador'), this.$t('country.equatorial_guinea'), this.$t('country.eritrea'),
        this.$t('country.estonia'), this.$t('country.eswatini_fmr_swaziland'), this.$t('country.ethiopia'), this.$t('country.fiji'),
        this.$t('country.finland'), this.$t('country.gabon'), this.$t('country.gambia'), this.$t('country.georgia'),
        this.$t('country.ghana'), this.$t('country.greece'), this.$t('country.grenada'), this.$t('country.guatemala'),
        this.$t('country.guinea'), this.$t('country.guinea_bissau'), this.$t('country.guyana'), this.$t('country.haiti'),
        this.$t('country.holy_see'), this.$t('country.honduras'), this.$t('country.hungary'), this.$t('country.iceland'),
        this.$t('country.india'), this.$t('country.indonesia'), this.$t('country.iran'), this.$t('country.iraq'),
        this.$t('country.ireland'), this.$t('country.israel'), this.$t('country.italy'), this.$t('country.ivory_coast'),
        this.$t('country.jamaica'), this.$t('country.japan'), this.$t('country.jordan'), this.$t('country.kazakhstan'),
        this.$t('country.kenya'), this.$t('country.kiribati'), this.$t('country.kuwait'), this.$t('country.kyrgyzstan'),
        this.$t('country.laos'), this.$t('country.latvia'), this.$t('country.lebanon'), this.$t('country.lesotho'),
        this.$t('country.liberia'), this.$t('country.libya'), this.$t('country.liechtenstein'), this.$t('country.lithuania'),
        this.$t('country.luxembourg'), this.$t('country.madagascar'), this.$t('country.malawi'), this.$t('country.malaysia'),
        this.$t('country.maldives'), this.$t('country.mali'), this.$t('country.malta'), this.$t('country.marshall_islands'),
        this.$t('country.mauritania'), this.$t('country.mauritius'), this.$t('country.mexico'), this.$t('country.micronesia'),
        this.$t('country.moldova'), this.$t('country.monaco'), this.$t('country.mongolia'), this.$t('country.montenegro'),
        this.$t('country.morocco'), this.$t('country.mozambique'), this.$t('country.myanmar_formerly_burma'), this.$t('country.namibia'),
        this.$t('country.nauru'), this.$t('country.nepal'), this.$t('country.netherlands'), this.$t('country.new_zealand'),
        this.$t('country.nicaragua'), this.$t('country.niger'), this.$t('country.nigeria'), this.$t('country.north_korea'),
        this.$t('country.north_macedonia'), this.$t('country.norway'), this.$t('country.oman'), this.$t('country.pakistan'),
        this.$t('country.palau'), this.$t('country.palestine_state'), this.$t('country.panama'), this.$t('country.papua_new_guinea'),
        this.$t('country.paraguay'), this.$t('country.peru'), this.$t('country.philippines'), this.$t('country.poland'),
        this.$t('country.portugal'), this.$t('country.qatar'), this.$t('country.romania'), this.$t('country.russia'),
        this.$t('country.rwanda'), this.$t('country.saint_kitts_and_nevis'), this.$t('country.saint_lucia'),
        this.$t('country.saint_vincent_and_the_grenadines'), this.$t('country.samoa'), this.$t('country.san_marino'),
        this.$t('country.sao_tome_and_principe'), this.$t('country.saudi_arabia'), this.$t('country.senegal'),
        this.$t('country.serbia'), this.$t('country.seychelles'), this.$t('country.sierra_leone'), this.$t('country.singapore'),
        this.$t('country.slovakia'), this.$t('country.slovenia'), this.$t('country.solomon_islands'), this.$t('country.somalia'),
        this.$t('country.south_africa'), this.$t('country.south_korea'), this.$t('country.south_sudan'), this.$t('country.spain'),
        this.$t('country.sri_lanka'), this.$t('country.sudan'), this.$t('country.suriname'), this.$t('country.sweden'),
        this.$t('country.syria'), this.$t('country.tajikistan'), this.$t('country.tanzania'), this.$t('country.thailand'),
        this.$t('country.timor_leste'), this.$t('country.togo'), this.$t('country.tonga'), this.$t('country.trinidad_and_tobago'),
        this.$t('country.tunisia'), this.$t('country.turkey'), this.$t('country.turkmenistan'), this.$t('country.tuvalu'),
        this.$t('country.uganda'), this.$t('country.ukraine'), this.$t('country.united_arab_emirates'), this.$t('country.uruguay'),
        this.$t('country.uzbekistan'), this.$t('country.vanuatu'), this.$t('country.venezuela'), this.$t('country.vietnam'),
        this.$t('country.yemen'), this.$t('country.zambia'), this.$t('country.zimbabwe')
      ].sort((a, b) => a.localeCompare(b));
    },
    isFormValid() {
      const data = this.getCurrentUserData();

      // Check common fields for all types
      if (!data.user.firstName || !data.user.lastName || !data.user.email) {
        return false;
      }

      // Check email format
      const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      if (!emailRegex.test(data.user.email)) {
        return false;
      }

      // Check address fields for all types
      if (!data.streetAddress || !data.city || !data.zipCode) {
        return false;
      }

      // Check specific fields based on customer type
      if (this.clientType === 'Organisation') {
        if (!data.name) return false;
      } else if (this.clientType === 'Company') {
        if (!data.name || !data.formOfBusiness || !data.vat) return false;
      }

      // Check privacy policy acceptance
      if (!this.privacyPolicyAccepted) {
        return false;
      }

      return true;
    }
  },
  methods: {
    getCurrentUserData() {
      if (this.clientType === 'Organisation') {
        return this.organisationData;
      } else if (this.clientType === 'Company') {
        return this.companyData;
      } else {
        return this.personalData;
      }
    },

    validateAndScrollToError() {
      this.errors = {};
      const data = this.getCurrentUserData();
      let firstErrorField = null;

      // Validate organisation/company name
      if (this.clientType === 'Organisation' && !data.name) {
        this.errors.organisationName = true;
        if (!firstErrorField) firstErrorField = 'organisationName';
      } else if (this.clientType === 'Company' && !data.name) {
        this.errors.companyName = true;
        if (!firstErrorField) firstErrorField = 'companyName';
      }

      // Validate business form (company only)
      if (this.clientType === 'Company' && !data.formOfBusiness) {
        this.errors.businessForm = true;
        if (!firstErrorField) firstErrorField = 'businessForm';
      }

      // Validate VAT (company only)
      if (this.clientType === 'Company' && !data.vat) {
        this.errors.vat = true;
        if (!firstErrorField) firstErrorField = 'vat';
      }

      // Validate address fields
      if (!data.streetAddress) {
        this.errors.streetAddress = true;
        if (!firstErrorField) firstErrorField = 'streetAddress';
      }
      if (!data.zipCode) {
        this.errors.zipCode = true;
        if (!firstErrorField) firstErrorField = 'zipCode';
      }
      if (!data.city) {
        this.errors.city = true;
        if (!firstErrorField) firstErrorField = 'city';
      }

      // Validate personal information
      if (!data.user.firstName) {
        this.errors.firstName = true;
        if (!firstErrorField) firstErrorField = 'firstName';
      }
      if (!data.user.lastName) {
        this.errors.lastName = true;
        if (!firstErrorField) firstErrorField = 'lastName';
      }
      if (!data.user.email) {
        this.errors.email = true;
        if (!firstErrorField) firstErrorField = 'email';
      } else {
        // Check email format
        const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
        if (!emailRegex.test(data.user.email)) {
          this.errors.email = true;
          if (!firstErrorField) firstErrorField = 'email';
        }
      }

      // Validate privacy policy acceptance
      if (!this.privacyPolicyAccepted) {
        this.errors.privacyPolicy = true;
        if (!firstErrorField) firstErrorField = 'privacyPolicy';
      }

      if (firstErrorField) {
        // Scroll to first error field
        this.$nextTick(() => {
          const element = document.querySelector(`[data-field="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({behavior: 'smooth', block: 'center'});
          }
        });
        return false;
      }

      return true;
    },

    completeRegistration() {
      if (this.$data.clientType === "PrivatePerson") {
        this.$data.personalData.name = this.personalData.user.firstName + " " + this.personalData.user.lastName;
      }
      if (!this.validateAndScrollToError()) {
        if (!this.privacyPolicyAccepted) {
          this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('privacy_policy_required'));
        } else {
          this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('error_fill_all_fields'));
        }
        return;
      }

      const data = this.getCurrentUserData();
      const registrationData = {
        clientType: this.clientType,
        name: data.name || null,
        streetAddress: data.streetAddress,
        zipCode: data.zipCode,
        city: data.city,
        country: data.country,
        phoneNumber: data.phoneNumber || null,
        vat: data.vat || null,
        formOfBusiness: data.formOfBusiness || null,
        user: {
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          email: data.user.email
        },
        language: this.$getBrowserLanguage()
      };

      this.isLoading = true;

      this.$axios.post('/api/register', registrationData)
          .then(response => {
            // Store email for resend functionality
            this.lastRegistrationEmail = data.user.email;
            // Show email confirmation screen
            this.registrationComplete = true;
            // Start the resend cooldown timer
            this.startResendCooldown();
          })
          .catch(error => {
            if (error.response && error.response.status === 409) {
              this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('email_already_exists'));
            } else {
              this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('registration_failed'));
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    startResendCooldown() {
      this.resendCooldown = 30;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
      }
      this.resendTimer = setInterval(() => {
        if (this.resendCooldown > 0) {
          this.resendCooldown--;
        } else {
          clearInterval(this.resendTimer);
          this.resendTimer = null;
        }
      }, 1000);
    },

    resendEmail() {
      if (this.resendCooldown > 0 || this.isResending || !this.lastRegistrationEmail) {
        return;
      }

      this.isResending = true;

      this.$axios.post('/api/register/resend', {
        email: this.lastRegistrationEmail,
        language: this.$getBrowserLanguage()
      })
          .then(response => {
            this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + this.$t('email_resent_success'));
            // Start the cooldown again
            this.startResendCooldown();
          })
          .catch(error => {
            if (error.response && error.response.status === 400) {
              this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('registration_already_completed'));
            } else {
              this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t('email_resend_failed'));
            }
          })
          .finally(() => {
            this.isResending = false;
          });
    }
  }
}
</script>

<style scoped>
/* Mobile responsive adjustments - reduce side padding for breathing space */
@media (max-width: 959px) {
  .col-10 {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .row.justify-content-center > .col-10 {
    padding-left: 28px !important;
    padding-right: 28px !important;
  }
}

@media (max-width: 600px) {
  .col-10 {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .row.justify-content-center > .col-10 {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }
}

@media (max-width: 400px) {
  .col-10 {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .row.justify-content-center > .col-10 {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}
</style>
